'use client';

import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface LightboxProps {
  isOpen: boolean;
  videoUrl: string;
  styleName: string;
  currentIndex: number;
  totalStyles: number;
  onClose: () => void;
  onNext: () => void;
  onPrevious: () => void;
}

const Lightbox: React.FC<LightboxProps> = ({
  isOpen,
  videoUrl,
  styleName,
  currentIndex,
  totalStyles,
  onClose,
  onNext,
  onPrevious,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    const handleArrowKeys = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        onNext();
      } else if (e.key === 'ArrowLeft') {
        onPrevious();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('keydown', handleArrowKeys);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('keydown', handleArrowKeys);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, onNext, onPrevious]);

  useEffect(() => {
    if (isOpen && videoRef.current) {
      setIsLoading(true);
      setHasError(false);
      videoRef.current.load();
      videoRef.current.play().catch(() => {
        setHasError(true);
        setIsLoading(false);
      });
    }
  }, [isOpen, videoUrl]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div data-component-id="src/app/homepage/components/Lightbox.tsx:80:4" data-component-path="src/app/homepage/components/Lightbox.tsx" data-component-line="80" data-component-end-line="153" data-component-file="Lightbox.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20inset-0%20z-50%20bg-black%2F95%20flex%20items-center%20justify-center%20p-4%22%7D" 
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      {/* Close Button */}
      <button data-component-id="src/app/homepage/components/Lightbox.tsx:85:6" data-component-path="src/app/homepage/components/Lightbox.tsx" data-component-line="85" data-component-end-line="91" data-component-file="Lightbox.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22absolute%20top-4%20right-4%20w-12%20h-12%20bg-card%2F80%20hover%3Abg-card%20rounded-full%20flex%20items-center%20justify-center%20backdrop-blur-sm%20transition-colors%20duration-300%20z-10%22%7D"
        onClick={onClose}
        className="absolute top-4 right-4 w-12 h-12 bg-card/80 hover:bg-card rounded-full flex items-center justify-center backdrop-blur-sm transition-colors duration-300 z-10"
        aria-label="Close lightbox"
      >
        <Icon data-component-id="src/app/homepage/components/Lightbox.tsx:90:8" data-component-path="src/app/homepage/components/Lightbox.tsx" data-component-line="90" data-component-end-line="90" data-component-file="Lightbox.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22XMarkIcon%22%2C%22className%22%3A%22text-foreground%22%7D" name="XMarkIcon" size={24} variant="outline" className="text-foreground" />
      </button>

      {/* Previous Button */}
      <button data-component-id="src/app/homepage/components/Lightbox.tsx:94:6" data-component-path="src/app/homepage/components/Lightbox.tsx" data-component-line="94" data-component-end-line="100" data-component-file="Lightbox.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22absolute%20left-4%20top-1%2F2%20-translate-y-1%2F2%20w-12%20h-12%20bg-card%2F80%20hover%3Abg-card%20rounded-full%20flex%20items-center%20justify-center%20backdrop-blur-sm%20transition-colors%20duration-300%20disabled%3Aopacity-50%20disabled%3Acursor-not-allowed%22%7D"
        onClick={onPrevious}
        className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-card/80 hover:bg-card rounded-full flex items-center justify-center backdrop-blur-sm transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label="Previous style"
      >
        <Icon data-component-id="src/app/homepage/components/Lightbox.tsx:99:8" data-component-path="src/app/homepage/components/Lightbox.tsx" data-component-line="99" data-component-end-line="99" data-component-file="Lightbox.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronLeftIcon%22%2C%22className%22%3A%22text-foreground%22%7D" name="ChevronLeftIcon" size={24} variant="outline" className="text-foreground" />
      </button>

      {/* Next Button */}
      <button data-component-id="src/app/homepage/components/Lightbox.tsx:103:6" data-component-path="src/app/homepage/components/Lightbox.tsx" data-component-line="103" data-component-end-line="109" data-component-file="Lightbox.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22absolute%20right-4%20top-1%2F2%20-translate-y-1%2F2%20w-12%20h-12%20bg-card%2F80%20hover%3Abg-card%20rounded-full%20flex%20items-center%20justify-center%20backdrop-blur-sm%20transition-colors%20duration-300%20disabled%3Aopacity-50%20disabled%3Acursor-not-allowed%22%7D"
        onClick={onNext}
        className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-card/80 hover:bg-card rounded-full flex items-center justify-center backdrop-blur-sm transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label="Next style"
      >
        <Icon data-component-id="src/app/homepage/components/Lightbox.tsx:108:8" data-component-path="src/app/homepage/components/Lightbox.tsx" data-component-line="108" data-component-end-line="108" data-component-file="Lightbox.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronRightIcon%22%2C%22className%22%3A%22text-foreground%22%7D" name="ChevronRightIcon" size={24} variant="outline" className="text-foreground" />
      </button>

      {/* Video Container */}
      <div data-component-id="src/app/homepage/components/Lightbox.tsx:112:6" data-component-path="src/app/homepage/components/Lightbox.tsx" data-component-line="112" data-component-end-line="152" data-component-file="Lightbox.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-6xl%20w-full%22%7D" className="max-w-6xl w-full">
        <div data-component-id="src/app/homepage/components/Lightbox.tsx:113:8" data-component-path="src/app/homepage/components/Lightbox.tsx" data-component-line="113" data-component-end-line="141" data-component-file="Lightbox.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20aspect-video%20bg-muted%20rounded-lg%20overflow-hidden%22%7D" className="relative aspect-video bg-muted rounded-lg overflow-hidden">
          {isLoading && (
            <div data-component-id="src/app/homepage/components/Lightbox.tsx:115:12" data-component-path="src/app/homepage/components/Lightbox.tsx" data-component-line="115" data-component-end-line="117" data-component-file="Lightbox.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20flex%20items-center%20justify-center%22%7D" className="absolute inset-0 flex items-center justify-center">
              <div data-component-id="src/app/homepage/components/Lightbox.tsx:116:14" data-component-path="src/app/homepage/components/Lightbox.tsx" data-component-line="116" data-component-end-line="116" data-component-file="Lightbox.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-16%20h-16%20border-4%20border-primary%20border-t-transparent%20rounded-full%20animate-spin%22%7D" className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}

          {hasError ? (
            <div data-component-id="src/app/homepage/components/Lightbox.tsx:121:12" data-component-path="src/app/homepage/components/Lightbox.tsx" data-component-line="121" data-component-end-line="124" data-component-file="Lightbox.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20flex%20flex-col%20items-center%20justify-center%20text-muted-foreground%22%7D" className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground">
              <Icon data-component-id="src/app/homepage/components/Lightbox.tsx:122:14" data-component-path="src/app/homepage/components/Lightbox.tsx" data-component-line="122" data-component-end-line="122" data-component-file="Lightbox.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ExclamationTriangleIcon%22%2C%22className%22%3A%22mb-4%20opacity-50%22%7D" name="ExclamationTriangleIcon" size={64} variant="outline" className="mb-4 opacity-50" />
              <p data-component-id="src/app/homepage/components/Lightbox.tsx:123:14" data-component-path="src/app/homepage/components/Lightbox.tsx" data-component-line="123" data-component-end-line="123" data-component-file="Lightbox.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%22%2C%22textContent%22%3A%22Video%20unavailable%22%7D" className="text-lg">Video unavailable</p>
            </div>
          ) : (
            <video data-component-id="src/app/homepage/components/Lightbox.tsx:126:12" data-component-path="src/app/homepage/components/Lightbox.tsx" data-component-line="126" data-component-end-line="139" data-component-file="Lightbox.tsx" data-component-name="video" data-component-content="%7B%22elementName%22%3A%22video%22%2C%22className%22%3A%22w-full%20h-full%20object-contain%22%2C%22src%22%3A%22%5Bvar%3AvideoUrl%5D%22%7D"
              ref={videoRef}
              className="w-full h-full object-contain"
              controls
              loop
              playsInline
              preload="metadata"
              src={videoUrl}
              onLoadedData={() => setIsLoading(false)}
              onError={() => {
                setHasError(true);
                setIsLoading(false);
              }}
            />
          )}
        </div>

        {/* Style Info */}
        <div data-component-id="src/app/homepage/components/Lightbox.tsx:144:8" data-component-path="src/app/homepage/components/Lightbox.tsx" data-component-line="144" data-component-end-line="151" data-component-file="Lightbox.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-6%20text-center%22%7D" className="mt-6 text-center">
          <h2 data-component-id="src/app/homepage/components/Lightbox.tsx:145:10" data-component-path="src/app/homepage/components/Lightbox.tsx" data-component-line="145" data-component-end-line="147" data-component-file="Lightbox.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-2xl%20md%3Atext-3xl%20font-heading%20font-bold%20text-foreground%20mb-2%22%7D" className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
            {styleName}
          </h2>
          <p data-component-id="src/app/homepage/components/Lightbox.tsx:148:10" data-component-path="src/app/homepage/components/Lightbox.tsx" data-component-line="148" data-component-end-line="150" data-component-file="Lightbox.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-muted-foreground%22%2C%22textContent%22%3A%22Style%20of%22%7D" className="text-muted-foreground">
            Style {currentIndex + 1} of {totalStyles}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Lightbox;